import { useAuth } from '../contexts/AuthContext';
import type { PlanSummary } from '../types/auth';

const DEFAULT_PLANOS_CONFIG = {
  basico: {
    modulos: ['principal', 'financeiro', 'cadastros_basicos', 'rh'],
  },
  profissional: {
    modulos: ['principal', 'financeiro', 'cadastros_basicos', 'frotas', 'comercial', 'tesouraria', 'rh'],
  },
  enterprise: {
    modulos: ['*'],
  },
} as const;

const MODULE_FEATURES: Record<string, string[]> = {
  principal: ['dashboard'],
  financeiro: [
    'transacoes',
    'fluxo_caixa',
    'fornecedores_clientes',
    'centros_custo',
    'formas_pagamento',
    'caixa_bancos',
    'grupos_contas',
    'plano_contas',
    'cartoes_credito',
    'transferencias',
    'pagamentos_parciais',
    'titulos_pagar',
    'titulos_receber',
    'orcamentos',
  ],
  fiscal: ['notas_fiscais', 'ctes', 'dre', 'seguradoras', 'tabelas_icms', 'config_fiscal'],
  cadastros_basicos: ['motoristas', 'veiculos', 'bloqueio_movimentacoes'],
  produtos_servicos: ['produtos', 'servicos'],
  frotas: ['frotas_dashboard', 'manutencao', 'ordens_servico', 'ordens_carregamento', 'abastecimento', 'viagens', 'contratos_frete', 'manifesto_frete', 'minutas_conhecimento', 'ciot_frota_propria', 'ciot_terceiros', 'veiculos', 'pneus', 'checklist', 'multas'],
  comercial: ['comercial_dashboard', 'vendas', 'clientes', 'calculadora_fretes', 'prefaturas'],
  tesouraria: ['extratos', 'conciliacao', 'integracao_bancaria', 'movimentacao_manual'],
  rh: [
    'funcionarios',
    'jornadas',
    'pontos',
    'banco_horas',
    'folhas_pagamento',
    'folha_dashboard',
    'rubricas',
    'rubricas_fixas',
    'verbas_variaveis',
    'adiantamentos',
  ],
  suprimentos: ['solicitacoes', 'cotacoes', 'pedidos_compra', 'recebimento_pedidos', 'estoque', 'almoxarifados', 'familias_produto', 'finalidades'],
  configuracoes: ['usuarios', 'empresas', 'filiais', 'grupos', 'sistema', 'config_sefaz', 'audit_logs', 'ajuda'],
  agentes_ia: ['copilot', 'ai_agents'],
};

// Features negadas por padrão para determinadas roles (sem permissão explícita)
const ROLE_DEFAULT_DENIED_FEATURES: Record<string, string[]> = {
  gestor: ['dashboard'],
};

const ALL_MODULES = Object.keys(MODULE_FEATURES);

const normalizeModules = (modules?: string[] | null) => {
  if (!modules) return [];
  const set = new Set<string>();
  modules.forEach((m) => {
    if (m) set.add(m.toLowerCase());
  });
  return Array.from(set);
};

const deriveFeatures = (modules: string[]) => {
  if (modules.includes('*')) return ['*'];
  const set = new Set<string>();
  modules.forEach((module) => {
    (MODULE_FEATURES[module] || []).forEach((feature) => set.add(feature));
  });
  return Array.from(set);
};

const getFallbackModules = (planKey?: string | null): string[] => {
  const key = (planKey || 'basico') as keyof typeof DEFAULT_PLANOS_CONFIG;
  const modulos = DEFAULT_PLANOS_CONFIG[key]?.modulos || DEFAULT_PLANOS_CONFIG.basico.modulos;
  return [...modulos];
};

const extractModules = (plan?: PlanSummary | null, fallbackKey?: string | null): string[] => {
  const normalized = normalizeModules(plan?.modulos);
  if (normalized.length > 0) return normalized;
  return getFallbackModules(fallbackKey);
};

export const useFeatureAccess = () => {
  const { user } = useAuth();

  if (!user) {
    return {
      hasFeature: () => false,
      hasModule: () => false,
      plano: null,
      getAvailableModules: () => [],
      getAvailableFeatures: () => [],
      requiresUpgrade: () => true,
      getRequiredPlano: () => null,
      getUserAllowedFeatures: () => [],
    };
  }

  const isSuperAdmin = (user.role || '').toUpperCase() === 'SUPER_ADMIN';
  const modules = extractModules(user.tenant_plan, user.tenant_plano);
  const planFeatures = (user.tenant_plan?.features && user.tenant_plan.features.length > 0
    ? user.tenant_plan.features
    : deriveFeatures(modules));

  // Features permitidas para o usuário (se vazio = todas do plano)
  const userAllowedFeatures = user.allowed_features || [];

  const hasModule = (module: string): boolean => {
    if (isSuperAdmin) return true;
    if (user.tenant_plan?.has_plan_selected === false) return false;
    if (modules.includes('*')) return true;
    return modules.includes(module);
  };

  const hasFeature = (feature: string): boolean => {
    if (isSuperAdmin) return true;

    // Verifica se feature está no plano
    const planHasFeature = planFeatures.includes('*') || planFeatures.includes(feature);
    if (!planHasFeature) return false;

    const userRole = (user.role || "").toLowerCase();
    const deniedByRole = ROLE_DEFAULT_DENIED_FEATURES[userRole] || [];

    // Features controladas por role: apenas a role restrita exige concessao explicita;
    // demais roles tem acesso livre independente do allowed_features
    const isRoleControlled = Object.values(ROLE_DEFAULT_DENIED_FEATURES).some((list) =>
      list.includes(feature)
    );
    if (isRoleControlled) {
      if (deniedByRole.includes(feature)) return userAllowedFeatures.includes(feature);
      return true;
    }

    // Features normais: allowed_features vazio = acesso livre ao plano
    if (userAllowedFeatures.length === 0) return true;
    return userAllowedFeatures.includes(feature);
  };

  const getAvailableModules = (): string[] => {
    if (modules.includes('*')) return ALL_MODULES;
    return modules;
  };

  const getAvailableFeatures = (): string[] => {
    if (planFeatures.includes('*')) {
      const all = new Set<string>();
      Object.values(MODULE_FEATURES).forEach((list) => list.forEach((f) => all.add(f)));
      return Array.from(all);
    }
    return planFeatures;
  };

  const getUserAllowedFeatures = (): string[] => {
    return userAllowedFeatures;
  };

  const requiresUpgrade = (feature: string): boolean => {
    if (isSuperAdmin) return false;
    // Verifica se o PLANO não tem a feature (precisa de upgrade)
    const planHasFeature = planFeatures.includes('*') || planFeatures.includes(feature);
    return !planHasFeature;
  };

  const getRequiredPlano = (featureOrModule: string): string | null => {
    const planos: (keyof typeof DEFAULT_PLANOS_CONFIG)[] = ['basico', 'profissional', 'enterprise'];
    for (const plano of planos) {
      const mods: string[] = [...DEFAULT_PLANOS_CONFIG[plano].modulos];
      const feats = deriveFeatures(mods);
      if (mods.includes('*') || feats.includes('*') || mods.includes(featureOrModule) || feats.includes(featureOrModule)) {
        return plano;
      }
    }
    return null;
  };

  return {
    hasFeature,
    hasModule,
    plano: user.tenant_plano || null,
    getAvailableModules,
    getAvailableFeatures,
    requiresUpgrade,
    getRequiredPlano,
    getUserAllowedFeatures,
  };
};

export const useFeatureBadge = (feature: string) => {
  const { hasFeature, getRequiredPlano } = useFeatureAccess();

  const hasAccess = hasFeature(feature);
  const requiredPlano = getRequiredPlano(feature);

  const badgeMap: Record<string, { label: string; color: string }> = {
    profissional: { label: 'PRO', color: 'blue' },
    enterprise: { label: 'ENTERPRISE', color: 'purple' },
  };

  const badge = requiredPlano && badgeMap[requiredPlano] ? badgeMap[requiredPlano] : null;

  return {
    hasAccess,
    badge,
    requiredPlano,
  };
};
